import React from 'react';

import { IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ExternalLink, Locate } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { ContractIcon } from 'src/components/icons/ContractIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { alineLinkButtonStylesWithUnderline } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  action: {
    flexShrink: 0,
  },
  chip: {
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderRadius: 4,
    color: theme.palette.primary.main,
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 700,
    height: 20,
    justifyContent: 'center',
    letterSpacing: '0.17px',
    padding: theme.spacing(0, 0.5),
  },
  icon: {
    display: 'flex',
    flexShrink: 0,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    textAlign: 'left',
    width: 320,
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  title: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(0.25),
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  titleLink: {
    ...alineLinkButtonStylesWithUnderline,
    background: 'none',
    border: 0,
    padding: 0,
    textAlign: 'left',
  },
  titleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

export interface CitationCardProps {
  /** Opens the cited spot. Same action as clicking the badge. */
  onViewInDocument: () => void;
  title: string;
  /** Second line under the name, such as "This document". */
  caption?: string | null;
  /** Marks the action button's tooltip as inside a parent popover. */
  cardClassName?: string;
  /** Role chip on the second line. Shown only when there is no caption. */
  chipLabel?: string | null;
  /** Crosshair when the citation is in the open document. Arrow-out otherwise. */
  jumpInPlace?: boolean;
}

/**
 * Hover card for a citation badge. Callers pass the document name, an optional
 * second line, and whether the action stays in the open document.
 */
export const CitationCard = React.memo(function CitationCard({
  caption,
  cardClassName,
  chipLabel,
  jumpInPlace,
  onViewInDocument,
  title,
}: CitationCardProps): JSX.Element {
  const { classes } = useStyles();
  const heading = title || 'Untitled Document';
  const actionLabel = jumpInPlace ? 'Jump to citation' : 'View in document';

  function handleView(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onViewInDocument();
  }

  return (
    <div className={classes.root}>
      <div className={classes.titleRow}>
        <span aria-hidden className={classes.icon}>
          <ContractIcon height={20} width={20} />
        </span>
        <div className={classes.title}>
          <Typography
            className={classes.titleLink}
            component="button"
            type="button"
            variant="subtitle2"
            onClick={handleView}
          >
            {heading}
          </Typography>
          {Boolean(caption) && (
            <Typography className={classes.subtitle} component="div" variant="caption">
              {caption}
            </Typography>
          )}
          {Boolean(chipLabel) && !caption && <span className={classes.chip}>{chipLabel}</span>}
        </div>
        <AlineTooltip
          arrow
          placement="top"
          slotProps={{ tooltip: { className: cardClassName } }}
          title={actionLabel}
        >
          <IconButton
            aria-label={actionLabel}
            className={classes.action}
            size="small"
            onClick={handleView}
          >
            <CitationCardActionIcon jumpInPlace={jumpInPlace} />
          </IconButton>
        </AlineTooltip>
      </div>
    </div>
  );
});

function CitationCardActionIcon({
  jumpInPlace,
}: {
  jumpInPlace: boolean | undefined;
}): JSX.Element {
  if (jumpInPlace) return <Locate color={IconColors.DarkGrey} size={16} />;

  return <ExternalLink color={IconColors.DarkGrey} size={16} />;
}
